import { type InfiniteData } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { getClientProfileList } from '@/api/client';
import { getAllRecruiters } from '@/api/recruiter';
import { loginAsUser, isAuthErrorResponse } from '@/api/user';
import {
  hardNavigateToDashboard,
  isAdminImpersonating,
  prepareReturnToAdminSession,
} from '@/components/admin/SwitchUser/switchRoleSession';
import {
  currentClientProfileOption,
  currentRecruiterOption,
  mergeOptionIfMissing,
  resolveImpersonationSelectValues,
} from '@/components/admin/SwitchUser/SwitchUserPopup/impersonationSelect';
import {
  getClientProfileId,
  setClientProfileId,
  setRecruiterCompanyId,
} from '@/config/axios';
import { useApiInfiniteQuery, useApiMutation } from '@/hooks/api';
import { LISTING_STALE_TIME } from '@/hooks/api/defaults';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { queryKeys } from '@/shared/lib/queryKeys';
import { parseCursorListingResponse } from '@/UI/filteredListing';
import { resolveRemoteUserTypeLabel } from '@/utils/atsRoleDisplay';
import {
  ADMIN_TOKEN_KEY,
  getAccessToken,
  getLocalStorageItem,
  storeAccessToken,
  storeAdminMarker,
  storeAdminToken,
  storeAdminUserId,
} from '@/utils/localStorage';

import type { ClientProfile } from '@/types/company';
import type { Recruiter } from '@/types/recruiter';
import type { ParsedCursorListingResult } from '@/UI/filteredListing';

const SEARCH_DEBOUNCE_MS = 300;
const RECRUITER_PAGE_SIZE = 20;

/** `clients/profiles/` is cursor-paginated (ClientProfilePagination). */
function buildClientProfileQuery(search: string, cursor?: string): string {
  const params = new URLSearchParams();
  if (search) {
    params.set('search', search);
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * `recruiters/` uses page-number pagination (RecruiterPageNumberPagination), so
 * `pagination.next` is the next page number rather than a cursor. Sending it
 * back as `?cursor=` is silently ignored and re-serves page one.
 */
function buildRecruiterQuery(search: string, page: number): string {
  const params = new URLSearchParams();
  if (search) {
    params.set('search', search);
  }
  params.set('page', String(page));
  params.set('limit', String(RECRUITER_PAGE_SIZE));
  return `?${params.toString()}`;
}

export function useSwitchUserPopup(isVisible: boolean) {
  const [selectedClientProfileId, selectClientProfileId] = useState('');
  const [selectedRecruiterId, selectRecruiterId] = useState('');

  const [clientSearch, setClientSearch] = useState('');
  const [recruiterSearch, setRecruiterSearch] = useState('');

  const { showError, showErrorFrom } = useApiFeedback();

  const { user, isAdmin, isClient, isRecruiter } = useCurrentUser();
  const canSwitchUsers = isAdmin || isAdminImpersonating();
  const isImpersonating = isAdminImpersonating();
  const hydratedOpenRef = useRef(false);
  const adminRequest = isImpersonating
    ? { useAdminSession: true as const }
    : undefined;

  const {
    data: clientProfilePages,
    hasNextPage: hasMoreClientProfiles,
    isFetchingNextPage: isFetchingMoreClientProfiles,
    fetchNextPage: fetchMoreClientProfiles,
  } = useApiInfiniteQuery<
    ParsedCursorListingResult<ClientProfile>,
    Error,
    InfiniteData<ParsedCursorListingResult<ClientProfile>>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: queryKeys.admin.switchUserClients(clientSearch),
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const res = await getClientProfileList(
        buildClientProfileQuery(clientSearch, pageParam),
        adminRequest,
      );
      return parseCursorListingResponse<ClientProfile>(res);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: isVisible && canSwitchUsers,
    staleTime: LISTING_STALE_TIME,
  });

  const {
    data: recruiterPages,
    hasNextPage: hasMoreRecruiters,
    isFetchingNextPage: isFetchingMoreRecruiters,
    fetchNextPage: fetchMoreRecruiters,
  } = useApiInfiniteQuery<
    ParsedCursorListingResult<Recruiter>,
    Error,
    InfiniteData<ParsedCursorListingResult<Recruiter>>,
    readonly unknown[],
    number
  >({
    queryKey: queryKeys.admin.switchUserRecruiters(recruiterSearch),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await getAllRecruiters(
        buildRecruiterQuery(recruiterSearch, pageParam),
        adminRequest,
      );
      return parseCursorListingResponse<Recruiter>(res);
    },
    // nextCursor holds the server's next page number here, or null when exhausted.
    getNextPageParam: (lastPage) =>
      lastPage.nextCursor == null ? undefined : Number(lastPage.nextCursor),
    enabled: isVisible && canSwitchUsers,
    staleTime: LISTING_STALE_TIME,
  });

  const clientProfiles = useMemo(
    () =>
      mergeOptionIfMissing(
        clientProfilePages?.pages.flatMap((page) => page.rows) ?? [],
        currentClientProfileOption(user, selectedClientProfileId),
      ),
    [clientProfilePages, selectedClientProfileId, user],
  );

  const recruiters = useMemo(
    () =>
      mergeOptionIfMissing(
        recruiterPages?.pages.flatMap((page) => page.rows) ?? [],
        currentRecruiterOption(user, selectedRecruiterId),
      ),
    [recruiterPages, selectedRecruiterId, user],
  );

  const handleSelectClientProfile = useCallback((profileId: string) => {
    selectClientProfileId(profileId);
    selectRecruiterId('');
  }, []);

  const handleSelectRecruiter = useCallback((recruiterId: string) => {
    selectClientProfileId('');
    selectRecruiterId(recruiterId);
  }, []);

  // Searching re-keys the infinite query, so the server refetches page one
  // rather than a parallel request racing the cached list.
  const debouncedSearchClientProfile = useMemo(
    () => debounce(setClientSearch, SEARCH_DEBOUNCE_MS),
    [],
  );

  const debouncedSearchRecruiter = useMemo(
    () => debounce(setRecruiterSearch, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearchClientProfile.cancel();
      debouncedSearchRecruiter.cancel();
    };
  }, [debouncedSearchClientProfile, debouncedSearchRecruiter]);

  useEffect(() => {
    if (!isVisible) {
      hydratedOpenRef.current = false;
      selectClientProfileId('');
      selectRecruiterId('');
      return;
    }
    if (hydratedOpenRef.current) {
      return;
    }
    if (isImpersonating && !user?.id) {
      return;
    }
    hydratedOpenRef.current = true;
    const values = resolveImpersonationSelectValues({
      isImpersonating,
      isClient,
      isRecruiter,
      userId: user?.id,
      clientProfileId: getClientProfileId(),
      availableClientProfileIds: (user?.available_client_profiles ?? []).map(
        (profile) => profile.id,
      ),
    });
    selectClientProfileId(values.selectedClientProfileId);
    selectRecruiterId(values.selectedRecruiterId);
  }, [
    isVisible,
    isImpersonating,
    isClient,
    isRecruiter,
    user?.id,
    user?.available_client_profiles,
  ]);

  const loadMoreClientProfiles = useCallback(() => {
    if (!hasMoreClientProfiles || isFetchingMoreClientProfiles) {
      return;
    }
    void fetchMoreClientProfiles();
  }, [
    hasMoreClientProfiles,
    isFetchingMoreClientProfiles,
    fetchMoreClientProfiles,
  ]);

  const loadMoreRecruiters = useCallback(() => {
    if (!hasMoreRecruiters || isFetchingMoreRecruiters) {
      return;
    }
    void fetchMoreRecruiters();
  }, [hasMoreRecruiters, isFetchingMoreRecruiters, fetchMoreRecruiters]);

  const getRemoteRoleLabel = useCallback(
    (profile: ClientProfile) =>
      profile.remote_role_name ??
      resolveRemoteUserTypeLabel(profile.remote_user_type),
    [],
  );

  const switchUserMutation = useApiMutation({
    mutationFn: ({
      userId,
      profileId,
    }: {
      userId?: string;
      profileId?: string;
    }) =>
      loginAsUser(
        userId,
        profileId,
        isAdminImpersonating() ? { useAdminSession: true } : undefined,
      ),
    onSuccess: (res) => {
      if (!res || isAuthErrorResponse(res) || !res.access_token) {
        showError('ERROR_MESSAGE');
        return;
      }

      const adminToken =
        getLocalStorageItem(ADMIN_TOKEN_KEY) || getAccessToken() || '';
      storeAdminToken(adminToken);

      storeAccessToken(res.access_token);
      storeAdminMarker();
      // Drop any previous agency workspace so the impersonated user starts clean.
      setRecruiterCompanyId(null);

      if (res.admin_user_id) {
        storeAdminUserId(res.admin_user_id);
      }

      if (res.client_profile_id) {
        setClientProfileId(res.client_profile_id);
      } else {
        setClientProfileId(null);
      }

      hardNavigateToDashboard();
    },
    onError: (err) => {
      showErrorFrom(err);
    },
  });

  const handleConfirm = useCallback(() => {
    if (!selectedClientProfileId && !selectedRecruiterId) {
      return;
    }
    switchUserMutation.mutate({
      userId: selectedRecruiterId || undefined,
      profileId: selectedClientProfileId || undefined,
    });
  }, [selectedClientProfileId, selectedRecruiterId, switchUserMutation]);

  const handleBackToAdminRole = useCallback(() => {
    prepareReturnToAdminSession();
    hardNavigateToDashboard();
  }, []);

  return {
    actions: {
      handleSearchClientProfile: debouncedSearchClientProfile,
      handleSearchRecruiter: debouncedSearchRecruiter,
      handleSelectClientProfile,
      handleSelectRecruiter,
      handleConfirm,
      handleBackToAdminRole,
      loadMoreClientProfiles,
      loadMoreRecruiters,
    },
    data: {
      clientProfiles,
      recruiters,
      selectedClientProfileId,
      selectedRecruiterId,
      loading: switchUserMutation.isPending,
      canSwitchUsers,
      isImpersonating,
      getRemoteRoleLabel,
    },
  };
}
